/**
 * useBulkLifecycleActions Hook
 *
 * Auto-generates bulk lifecycle actions from lifecycle metadata.
 * Provides execution logic for sequential API calls with progress tracking.
 *
 * This hook is INTERNAL to MetadataTable - not exported publicly.
 */

import { useState, useMemo, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apiService } from '@/services/apiService'
import type { LifecycleMetadata } from '@/services/metadataService'
import type { FailedItem } from '../components/BulkOperationResults'

/**
 * Configuration for useBulkLifecycleActions hook
 */
export interface UseBulkLifecycleActionsOptions {
  /** API endpoint (e.g., '/offerings') */
  endpoint: string
  /** Lifecycle metadata from backend */
  lifecycle: LifecycleMetadata | undefined
  /** All data records currently loaded */
  data: Record<string, unknown>[]
  /** Currently selected row keys */
  selectedRowKeys: string[]
  /** Field name for row key (default: 'uid') */
  keyField?: string
  /** React Query key for cache invalidation */
  queryKey?: string[]
  /** Callback after successful bulk operation */
  onSuccess?: () => void
  /**
   * Whether viewing deleted records (trash mode).
   * When true, ALL lifecycle actions are hidden because:
   * - Deleted records cannot have their lifecycle state changed
   * - They must be restored (undeleted) first
   * Default: false
   */
  showDeleted?: boolean
  /**
   * Handler for bulk duplicate (opensEditor actions with multiple selection).
   * Called with a record's data, should return the payload for creating a draft copy.
   * If not provided, opensEditor actions with multiple selection will fail gracefully.
   */
  getDuplicatePayload?: (record: Record<string, unknown>) => Record<string, unknown>
}

/**
 * A single bulk lifecycle action with computed applicability
 */
export interface BulkLifecycleAction {
  /** Action key (e.g., 'activate', 'expire') */
  key: string
  /** Button label */
  label: string
  /** Ant Design icon name */
  icon?: string
  /** Button type */
  buttonType?: 'primary' | 'default' | 'dashed' | 'link' | 'text'
  /** Whether this is a destructive action */
  danger?: boolean
  /** Number of selected items where action is valid */
  applicableCount: number
  /** Number of selected items where action is NOT valid */
  skippedCount: number
  /** Button is disabled if no applicable items */
  disabled: boolean
  /** Original fromStates for display purposes */
  fromStates: string[]
  /** Target state after action */
  toState: string | null
  /** Confirmation message template */
  confirmationMessage?: string | null
  /** Success message template */
  successMessage?: string
  /** Display order for consistent button positioning (lower = first). Lifecycle: 10-90, Record: 100+ */
  order?: number
  /**
   * Whether this action opens an editor (e.g., duplicate).
   * When true with single selection, opens drawer instead of executing bulk action.
   * When true with multiple selection, executes bulk operation (e.g., bulk duplicate creates drafts).
   */
  opensEditor?: boolean
}

/**
 * Progress state during bulk execution
 */
export interface BulkProgress {
  /** Whether operation is in progress */
  isRunning: boolean
  /** Action being executed */
  operation: string
  /** Total items to process */
  total: number
  /** Completed successfully */
  completed: number
  /** Failed count */
  failed: number
}

/**
 * Results after bulk execution completes
 */
export interface BulkResults {
  /** Whether to show results modal */
  visible: boolean
  /** Operation type */
  operation: string
  /** Total items attempted */
  total: number
  /** Items that succeeded */
  succeeded: number
  /** Items that failed */
  failed: number
  /** Details of failed items */
  failedItems: FailedItem[]
  /** Whether counts are confirmed by server metadata */
  fromServerMeta: boolean
  /** State before the action (aggregated from responses) */
  previousState?: string
  /** State after the action (aggregated from responses) */
  newState?: string
  /** Total records superseded across all operations */
  supersededCount?: number
  /** Total continuations created across all operations */
  continuationsCreated?: number
}

/**
 * Return type from useBulkLifecycleActions hook
 */
export interface UseBulkLifecycleActionsResult {
  /** Whether bulk lifecycle actions are available */
  enabled: boolean
  /** Auto-generated bulk actions from lifecycle metadata */
  bulkActions: BulkLifecycleAction[]
  /** Execute a bulk action by key */
  execute: (actionKey: string) => Promise<void>
  /** Cancel an in-progress operation */
  cancel: () => void
  /** Current progress state */
  progress: BulkProgress
  /** Results from last completed operation */
  results: BulkResults
  /** Close the results modal */
  closeResults: () => void
}

/**
 * Hook for auto-generating bulk lifecycle actions from metadata
 *
 * @example
 * ```tsx
 * const { enabled, bulkActions, execute, progress, results } = useBulkLifecycleActions({
 *   endpoint: '/offerings',
 *   lifecycle: metadata.lifecycle,
 *   data,
 *   selectedRowKeys,
 *   onSuccess: () => refetch(),
 * })
 *
 * // Render buttons
 * {bulkActions.map(action => (
 *   <Button
 *     key={action.key}
 *     disabled={action.disabled}
 *     onClick={() => execute(action.key)}
 *   >
 *     {action.label} ({action.applicableCount})
 *   </Button>
 * ))}
 * ```
 */
export function useBulkLifecycleActions({
  endpoint,
  lifecycle,
  data,
  selectedRowKeys,
  keyField = 'uid',
  queryKey,
  onSuccess,
  showDeleted = false,
  getDuplicatePayload,
}: UseBulkLifecycleActionsOptions): UseBulkLifecycleActionsResult {
  const queryClient = useQueryClient()
  const cancelRef = useRef(false)

  // Progress state
  const [progress, setProgress] = useState<BulkProgress>({
    isRunning: false,
    operation: '',
    total: 0,
    completed: 0,
    failed: 0,
  })

  // Results state
  const [results, setResults] = useState<BulkResults>({
    visible: false,
    operation: '',
    total: 0,
    succeeded: 0,
    failed: 0,
    failedItems: [],
    fromServerMeta: false,
    previousState: undefined,
    newState: undefined,
    supersededCount: undefined,
    continuationsCreated: undefined,
  })

  // Create a lookup map for selected records
  const selectedRecordsMap = useMemo(() => {
    const map = new Map<string, Record<string, unknown>>()
    for (const record of data) {
      const key = String(record[keyField] ?? '')
      if (selectedRowKeys.includes(key)) {
        map.set(key, record)
      }
    }
    return map
  }, [data, selectedRowKeys, keyField])

  // Determine if bulk lifecycle is enabled
  const enabled = Boolean(
    lifecycle?.enabled &&
    lifecycle?.actions &&
    Object.keys(lifecycle.actions).length > 0
  )

  // Auto-generate bulk actions from lifecycle metadata
  const bulkActions = useMemo<BulkLifecycleAction[]>(() => {
    if (!enabled || !lifecycle) {
      return []
    }

    // When viewing deleted records (trash mode), hide ALL lifecycle actions
    // Deleted records cannot have their lifecycle state changed - they must be restored first
    if (showDeleted) {
      return []
    }

    const stateField = lifecycle.stateField
    const actions: BulkLifecycleAction[] = []

    for (const [actionKey, actionConfig] of Object.entries(lifecycle.actions)) {
      // Compute applicability for selected items
      let applicableCount = 0
      let skippedCount = 0

      for (const recordKey of selectedRowKeys) {
        const record = selectedRecordsMap.get(recordKey)
        if (record) {
          const currentState = record[stateField] as string | undefined
          if (currentState && actionConfig.fromStates.includes(currentState)) {
            applicableCount++
          } else {
            skippedCount++
          }
        }
      }

      actions.push({
        key: actionKey,
        label: actionConfig.label,
        icon: actionConfig.icon,
        buttonType: actionConfig.buttonType,
        danger: actionConfig.danger,
        applicableCount,
        skippedCount,
        disabled: applicableCount === 0,
        fromStates: actionConfig.fromStates,
        toState: actionConfig.toState,
        confirmationMessage: actionConfig.confirmationMessage,
        successMessage: actionConfig.successMessage,
        order: actionConfig.order,
        // Include opensEditor flag so caller can handle single vs multi selection differently
        opensEditor: actionConfig.opensEditor,
      })
    }

    return actions
  }, [enabled, lifecycle, selectedRowKeys, selectedRecordsMap, showDeleted])

  // Execute a bulk action
  const execute = useCallback(async (actionKey: string) => {
    if (!lifecycle) return

    const action = bulkActions.find(a => a.key === actionKey)
    if (!action || action.applicableCount === 0) return

    const stateField = lifecycle.stateField
    cancelRef.current = false

    // Get applicable records
    const applicableRecords: Record<string, unknown>[] = []
    for (const recordKey of selectedRowKeys) {
      const record = selectedRecordsMap.get(recordKey)
      if (record) {
        const currentState = record[stateField] as string | undefined
        if (currentState && action.fromStates.includes(currentState)) {
          applicableRecords.push(record)
        }
      }
    }

    // Initialize progress
    setProgress({
      isRunning: true,
      operation: action.label,
      total: applicableRecords.length,
      completed: 0,
      failed: 0,
    })

    let completed = 0
    let failed = 0
    const failedItems: FailedItem[] = []

    // Track lifecycle metadata from responses
    let hasServerMeta = false
    let aggregatedPreviousState: string | undefined
    let aggregatedNewState: string | undefined
    let totalSuperseded = 0
    let totalContinuations = 0

    // Execute sequentially
    for (const record of applicableRecords) {
      // Check for cancellation
      if (cancelRef.current) {
        break
      }

      const recordUid = String(record[keyField] ?? '')
      const recordName = String(record['name'] ?? record['label'] ?? recordUid)

      try {
        let response

        // Handle opensEditor actions (like duplicate) - create new records via POST
        if (action.opensEditor) {
          if (!getDuplicatePayload) {
            // No handler provided - skip this record
            failed++
            failedItems.push({
              uid: recordUid,
              name: recordName,
              error: 'Bulk duplicate not configured',
            })
            continue
          }

          // Get the filtered payload for creating a draft copy
          const createPayload = getDuplicatePayload(record)
          response = await apiService.post<unknown>(endpoint, createPayload)
        } else {
          // Standard lifecycle action endpoint
          response = await apiService.post<unknown>(
            `${endpoint}/${recordUid}/${actionKey}`,
            {}
          )
        }

        if (response.success) {
          completed++

          // Extract lifecycle metadata from response.meta (not applicable for duplicate)
          if (!action.opensEditor) {
            const meta = response.meta as Record<string, unknown> | undefined
            if (meta) {
              hasServerMeta = true

              // Track state transitions (use first response's states as representative)
              if (!aggregatedPreviousState && meta.previousState) {
                aggregatedPreviousState = String(meta.previousState)
              }
              if (!aggregatedNewState && meta.newState) {
                aggregatedNewState = String(meta.newState)
              }

              // Aggregate supersession and continuation counts
              if (typeof meta.supersededCount === 'number') {
                totalSuperseded += meta.supersededCount
              }
              if (typeof meta.continuationsCreated === 'number') {
                totalContinuations += meta.continuationsCreated
              }
            }
          }
        } else {
          failed++
          failedItems.push({
            uid: recordUid,
            name: recordName,
            error: response.errors?.[0]?.message ?? 'Unknown error',
          })
        }
      } catch (error) {
        failed++
        failedItems.push({
          uid: recordUid,
          name: recordName,
          error: error instanceof Error ? error.message : 'Unknown error',
        })
      }

      // Update progress
      setProgress(prev => ({
        ...prev,
        completed,
        failed,
      }))
    }

    // Clear progress
    setProgress({
      isRunning: false,
      operation: '',
      total: 0,
      completed: 0,
      failed: 0,
    })

    // Show results with lifecycle metadata
    setResults({
      visible: true,
      operation: action.label,
      total: applicableRecords.length,
      succeeded: completed,
      failed,
      failedItems,
      fromServerMeta: hasServerMeta,
      previousState: aggregatedPreviousState,
      newState: aggregatedNewState,
      supersededCount: totalSuperseded > 0 ? totalSuperseded : undefined,
      continuationsCreated: totalContinuations > 0 ? totalContinuations : undefined,
    })

    // Invalidate queries to refetch data
    const key = queryKey || ['tableData', endpoint]
    queryClient.invalidateQueries({ queryKey: key })

    // Also invalidate record detail queries
    queryClient.invalidateQueries({
      queryKey: ['record', endpoint],
      exact: false,
    })

    // Call success callback if any items succeeded
    if (completed > 0) {
      onSuccess?.()
    }
  }, [lifecycle, bulkActions, selectedRowKeys, selectedRecordsMap, keyField, endpoint, queryKey, queryClient, onSuccess, getDuplicatePayload])

  // Cancel operation
  const cancel = useCallback(() => {
    cancelRef.current = true
  }, [])

  // Close results modal
  const closeResults = useCallback(() => {
    setResults({
      visible: false,
      operation: '',
      total: 0,
      succeeded: 0,
      failed: 0,
      failedItems: [],
      fromServerMeta: false,
      previousState: undefined,
      newState: undefined,
      supersededCount: undefined,
      continuationsCreated: undefined,
    })
  }, [])

  return {
    enabled,
    bulkActions,
    execute,
    cancel,
    progress,
    results,
    closeResults,
  }
}
